import { forwardRef, useEffect, useRef, useImperativeHandle, type ClipboardEvent, type KeyboardEvent } from 'react'
import { cn } from '@/lib/utils'

type OtpInputProps = {
  length?: number
  value: string
  onChange: (val: string) => void
  onComplete?: (val: string) => void
  autoFocus?: boolean
  disabled?: boolean
  hasError?: boolean
}

export type OtpInputHandle = {
  focus: () => void
  clear: () => void
}

/**
 * Codice OTP a N caselle (default 6).
 * - `autoComplete="one-time-code"` permette al browser di auto-compilare
 *   da SMS (iOS Safari, Chrome Android moderno).
 * - Paste su qualsiasi cella distribuisce le cifre.
 * - Backspace su cella vuota torna alla precedente.
 */
export const OtpInput = forwardRef<OtpInputHandle, OtpInputProps>(function OtpInput(
  { length = 6, value, onChange, onComplete, autoFocus, disabled, hasError },
  ref,
) {
  const inputsRef = useRef<(HTMLInputElement | null)[]>([])

  useImperativeHandle(ref, () => ({
    focus: () => inputsRef.current[0]?.focus(),
    clear: () => {
      onChange('')
      inputsRef.current[0]?.focus()
    },
  }))

  useEffect(() => {
    if (autoFocus) inputsRef.current[0]?.focus()
  }, [autoFocus])

  function setDigit(index: number, digit: string) {
    const clean = digit.replace(/\D/g, '').slice(0, 1)
    const arr = value.padEnd(length, ' ').split('')
    arr[index] = clean || ' '
    const next = arr.join('').replace(/ /g, '').slice(0, length)
    onChange(next)
    if (clean && index < length - 1) {
      inputsRef.current[index + 1]?.focus()
    }
    if (next.length === length) onComplete?.(next)
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>, index: number) {
    if (e.key === 'Backspace' && !value[index] && index > 0) {
      e.preventDefault()
      const arr = value.split('')
      arr.splice(index - 1, 1)
      onChange(arr.join(''))
      inputsRef.current[index - 1]?.focus()
    }
    if (e.key === 'ArrowLeft' && index > 0) inputsRef.current[index - 1]?.focus()
    if (e.key === 'ArrowRight' && index < length - 1) inputsRef.current[index + 1]?.focus()
  }

  function onPaste(e: ClipboardEvent<HTMLInputElement>) {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length)
    onChange(pasted)
    if (pasted.length === length) {
      inputsRef.current[length - 1]?.focus()
      onComplete?.(pasted)
    } else {
      inputsRef.current[pasted.length]?.focus()
    }
  }

  return (
    <div className="flex gap-2" onPaste={onPaste}>
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={(el) => {
            inputsRef.current[i] = el
          }}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          disabled={disabled}
          value={value[i] ?? ''}
          onChange={(e) => setDigit(i, e.target.value)}
          onKeyDown={(e) => onKeyDown(e, i)}
          className={cn(
            'h-12 w-10 rounded-md border-2 bg-background text-center text-lg font-semibold tabular-nums',
            'focus-visible:outline-none focus-visible:border-secondary',
            hasError ? 'border-destructive' : 'border-input',
            disabled && 'opacity-50',
          )}
        />
      ))}
    </div>
  )
})
